import { Component, Input, OnInit, OnDestroy, HostListener, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { EdpModalService } from "@edp-components/edp-modal";
import { ICampaign } from "@edp-models/edp-campaigns.model";
import { RouteStateService } from "@edp-services/route-state.service";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { getShowCelebrationZs } from "@edp-store/selectors/user.selectors";
import { environment } from "@edp-environments/environment";
import { getCampaigns } from "@edp-store/selectors/dashboard.selectors";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { SlickCarouselComponent } from "ngx-slick-carousel";
import { ExternalLinkService } from "@edp-directives/external-link/external-link.service";
import { BannerConditionService } from "@edp-components/edp-card-widgets/edp-widget-banner/banner-condition.service";
import { CO2Data } from "@edp-models/co2.model";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { EdpEConsentModalComponent } from "@edp-components/edp-e-consent-modal/edp-e-consent-modal.component";
import { CampaignModalComponent } from "@edp-components/campaign-modal/campaign-modal.component";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { getContractEs } from "@edp-store/selectors/ES/contract-es.selectors";
import { ContractEs } from "@edp-models/ES/es-contract.model";
import { EnvService } from "@edp-services/env.service";
import { getAdditionalProductEs } from "@edp-store/selectors/ES/product-es.selectors";
import { EsAdditionalProduct } from "@edp-models/ES/es-product.model";
import { BannerConditionESService } from "../banner-condition-es.service";

@Component({
    selector: "dashboard-header-es",
    templateUrl: "./dashboard-header-es.component.html",
    styleUrls: ["./dashboard-header-es.component.scss"],
})
export class DashboardHeaderESComponent implements OnInit, OnDestroy {
    @ViewChild("slickModal") slickModal: SlickCarouselComponent;
    @ViewChild("slickModalMobile") slickModalMobile: SlickCarouselComponent;

    public campaigns: ICampaign[] = [];
    public contract: ContractEs;
    public isCollapsed: boolean;
    public isUserAppE: boolean;
    public isApp: boolean;
    public mobileSize = false;
    public actualCampaignImage: string;
    public slideConfig: object = {};
    public mobileImage: string;
    public co2: CO2Data;

    private unsubscribe = new Subject();
    private timer: ReturnType<typeof setInterval>;
    private currentSlide = 0;
    private currentSlideMobile = 0;
    private pauseTimer = false;
    private executedCelebration = false;
    private executedPackSimulator: string | null = null;

    private genericModalOptions: NgbModalOptions = {
        windowClass: "edp-dialog-fullscreen",
    };

    private genericModalData: IModalData = {
        showClose: true,
    };

    constructor(
        private readonly router: Router,
        private readonly routerService: RouteStateService,
        private readonly edpModalService: EdpModalService,
        private readonly store: Store<IAppState>,
        private readonly storeES: Store<IAppEsState>,
        private readonly externalLink: ExternalLinkService,
        private readonly bannerConditionService: BannerConditionESService,
        private readonly operationHistoryService: OperationHistoryService,
        private envService: EnvService
    ) {
        this.isApp = environment.isApp;
        this.storeES
            .select(getContractEs)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(contract => {
                this.contract = contract;
            });
    }

    @HostListener("window:resize")
    public onResize() {
        this.mobileSize = false;
        if (window.innerWidth < SCREEN_SIZES.WDGT) {
            this.mobileSize = true;
        }
    }

    ngOnInit() {
        this.slideConfig = {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: true,
            arrows: false,
            dots: true,
            variableWidth: false,
            variableHeight: true,
            focusOnSelect: false,
            autoplay: false,
            autoplaySpeed: 5000,
            responsive: [
                {
                    breakpoint: 768,
                    settings: {
                        variableWidth: true,
                        centerMode: false,
                        dots: false,
                        infinite: false,
                    },
                },
            ],
        };

        this.onResize();

        const allowCampaignTypes = ["BANNER", "CONSENT"];
        combineLatest([this.store.select(getCampaigns), this.storeES.select(getContractEs), this.storeES.select(getAdditionalProductEs)])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(([campaigns, _, __]: [ICampaign[], ContractEs, EsAdditionalProduct]) => {
                if (campaigns) {
                    const campaignList: ICampaign[] = campaigns.filter(campaign => {
                        return !!allowCampaignTypes.includes(campaign.type) && this.bannerConditionService.validate(campaign.conditions);
                    });

                    if (campaignList.length > 0) {
                        this.campaigns = campaignList;
                        this.actualCampaignImage = this.getCampaignImage(campaignList[0]);
                        this.startTimer();
                    }
                }
            });
    }

    public afterChange(e) {
        this.actualCampaignImage = this.getCampaignImage(this.campaigns[e.nextSlide]);
    }

    public setSliderImages(campaign: ICampaign) {
        return (this.mobileImage = `${this.envService.appUrl()}/resource/image/${campaign.imgMobile}`);
    }

    public trackCampaign(campaign: ICampaign) {
        return campaign.id;
    }

    private getCampaignImage(campaign: ICampaign): string {
        return `${this.envService.appUrl()}/resource/image/${this.mobileSize ? campaign.imgMobile : campaign?.imgDesktopSmall}`;
    }

    public showContractList(canClose: boolean) {
        this.router.navigate([`locais-consumo/${this.contract.id}/inicio`]);
    }

    public async showCampaignDetails(campaign: ICampaign) {
        if (campaign.type === "CONSENT") {
            this.genericModalData.collection = campaign;
            this.pauseTimer = true;
            this.edpModalService
                .custom(EdpEConsentModalComponent, this.genericModalOptions, this.genericModalData)
                .then(() => (this.pauseTimer = false));
        } else if (campaign.typeLink === "FUNCIONALIDADE" || !campaign.linkUrl) {
            this.router.navigateByUrl(await this.routerService.internalPathTemplateToURLPath(campaign.linkFunctionality.routeConfig.route));
        } else {
            if (campaign.newTab) {
                this.externalLink.externalLink(campaign.linkUrl);
            } else {
                window.open(campaign.linkUrl, "_self");
            }
        }
    }

    private resetSlide() {
        clearInterval(this.timer);
        if (this.currentSlide > 0) {
            this.slickModal.slickGoTo(0);
            this.slickModalMobile.slickGoTo(0);
        }
        this.currentSlide = 0;
        this.currentSlideMobile = 0;
    }

    private startTimer(): void {
        this.resetSlide();
        this.timer = setInterval(() => {
            if (!this.pauseTimer) {
                this.currentSlide++;
                if (!this.campaigns[this.currentSlide]) {
                    this.currentSlide = 0;
                }
                this.slickModal.slickGoTo(this.currentSlide);

                this.currentSlideMobile++;
                if (!this.slickModalMobile.slides[this.currentSlideMobile]) {
                    this.currentSlideMobile = 0;
                }
                this.slickModalMobile.slickGoTo(this.currentSlideMobile);
            }
        }, 5000);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        clearInterval(this.timer);
    }
}
